<template>
  <div class="links-wrapper tab-panel-body">
    <!-- Header -->
    <div class="config-section-header">
      <div class="config-section-header__left">
        <v-icon small class="mr-2" color="text_secondary">mdi-link-variant</v-icon>
        <span class="config-section-label">Links de visualização</span>
        <span v-if="token" class="sc-badge sc-badge--primary ml-2">Ativo</span>
      </div>
      <v-btn
        small
        :color="token ? 'primary' : 'primary'"
        :outlined="!!token"
        @click="gerarLink"
        :loading="loadingToken"
        class="links-btn"
      >
        <v-icon small class="mr-1">{{ token ? 'mdi-refresh' : 'mdi-key-plus' }}</v-icon>
        {{ token ? 'Regerar' : 'Gerar link' }}
      </v-btn>
    </div>

    <!-- Link list -->
    <div v-if="token" class="links-list">
      <div
        v-for="(item, idx) in linkTypes"
        :key="idx"
        class="links-item"
      >
        <div class="links-item-left">
          <div class="links-icon-box">
            <v-icon small>{{ getLinkIcon(item) }}</v-icon>
          </div>
          <span class="links-item-name">{{ item }}</span>
          <template v-if="item === 'noticias'">
            <div class="links-portal-group">
              <v-btn-toggle v-model="portal" mandatory dense class="links-portal-toggle">
                <v-btn
                  v-for="p in portais"
                  :key="p.value"
                  :value="p.value"
                  x-small
                >
                  {{ p.text }}
                </v-btn>
              </v-btn-toggle>
            </div>
          </template>
        </div>
        <div class="links-item-right">
          <span class="links-url" @click="copyLinkToClipboard(getLinkUrl(item))">
            {{ getLinkUrl(item) }}
          </span>
          <v-btn
            icon
            x-small
            class="links-copy-btn"
            @click="copyLinkToClipboard(getLinkUrl(item))"
          >
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- No token -->
    <div v-else class="sc-empty-state">
      <v-icon size="36" color="text_secondary" class="mb-2">mdi-link-off</v-icon>
      <span class="sc-empty-state__title">Nenhum link gerado</span>
      <span class="sc-empty-state__subtitle">Clique em "Gerar link" para criar URLs de visualização</span>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { TOKEN_ROTATED_EVENT } from "@/components/GloboGraphics/GloboGraphicsPreviewLink.vue";

export default {
  name: "ModalControleTokenServico",
  props: {
    producao: Object,
    linkTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      token: null,
      loadingToken: false,
      portal: "g1",
      portais: [
        { text: "G1", value: "g1" },
        { text: "GE", value: "ge" },
        { text: "GSHOW", value: "gshow" },
      ],
    };
  },
  async created() {
    // Symmetric to the emit in gerarLink: the toolbar can rotate the same token while
    // this modal is open, and this copy would otherwise go stale the same way.
    this.$root.$on(TOKEN_ROTATED_EVENT, this.onTokenRotated);
    try {
      const { data } = await apiClient.get(`tokenServico/${this.producao.cod_producao}`);
      this.token = data.token ?? null;
    } catch {
      this.token = null;
    }
  },
  beforeDestroy() {
    this.$root.$off(TOKEN_ROTATED_EVENT, this.onTokenRotated);
  },
  computed: {
    domain() {
      const currentEnv = process.env.VUE_APP_ENV;
      return currentEnv === "hml"
        ? "socialcam.h.apps.g.globo"
        : "socialcam.apps.g.globo";
    },
  },
  methods: {
    getLinkIcon(item) {
      const icons = {
        tarja: "mdi-television",
        "tarja-cameras": "mdi-video",
        mosaico: "mdi-view-grid",
        noticias: "mdi-newspaper",
        enquete: "mdi-poll",
        cena: "mdi-palette",
      };
      return icons[item] || "mdi-link-variant";
    },
    getLinkUrl(item) {
      const base = this.domain;
      const cod = this.producao.cod_producao;
      const token = this.token;
      const routes = {
        tarja: `${base}/tarja?cod_producao=${cod}&token=${token}`,
        "tarja-cameras": `${base}/tarja?cod_producao=${cod}&mode=cam&token=${token}`,
        mosaico: `${base}/mosaico?cod_producao=${cod}&token=${token}`,
        noticias: `${base}/noticias?cod_producao=${cod}&cod_portal=${this.portal}&token=${token}`,
        enquete: `${base}/enquete?cod_producao=${cod}&token=${token}`,
        cena: `${base}/cena?cod_producao=${cod}&token=${token}`,
      };
      return routes[item] || `${base}/${item}?cod_producao=${cod}&token=${token}`;
    },
    copyLinkToClipboard(link) {
      if (!this.token) return;
      try {
        navigator.clipboard.writeText(link);
        this.$toast.info("Link copiado para a área de transferência");
      } catch (error) {
        this.$toast.error("Erro ao copiar link");
      }
    },
    onTokenRotated({ codProducao, token }) {
      if (String(codProducao) !== String(this.producao?.cod_producao)) return;
      this.token = token ?? null;
    },
    // Rotate, never DELETE-then-POST. The old sequence destroyed the working token
    // first, so a failure on the second call left the production with NO token at
    // all and every /cena, /tarja and /mosaico page already on air went dark.
    // Rotating replaces the row in one update: if it fails, the previous token is
    // untouched and still valid — which is why the catch below must NOT clear it.
    async gerarLink() {
      this.loadingToken = true;
      try {
        const { data } = await apiClient.post(
          `tokenServico/${this.producao.cod_producao}/rotate`
        );
        this.token = data.token;
        // The Globo Graphics toolbar shows the same token and holds it in its own
        // component state, so without this it keeps displaying and copying the link
        // this rotation just revoked — for the rest of the session, since the config
        // modal is opened from the GG page itself and nothing navigates.
        this.$root.$emit(TOKEN_ROTATED_EVENT, {
          codProducao: this.producao.cod_producao,
          token: data.token,
        });
        this.$toast.success("Link gerado com sucesso.");
      } catch (error) {
        this.$toast.error("Erro ao gerar link.");
      } finally {
        this.loadingToken = false;
      }
    },
  },
  watch: {
    async producao() {
      try {
        const { data } = await apiClient.get(`tokenServico/${this.producao.cod_producao}`);
        this.token = data.token ?? null;
      } catch {
        this.token = null;
      }
      this.portal = "g1";
    },
  },
};
</script>

<style lang="scss" scoped>
.links-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  border-radius: var(--sc-radius-md);
}

/* Link list */
.links-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.links-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: var(--sc-surface-tint-subtle);
  border-radius: var(--sc-radius-md);
  gap: 12px;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
  }
}

.links-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.links-icon-box {
  width: 32px;
  height: 32px;
  border-radius: var(--sc-radius-md);
  background: var(--sc-surface-tint-hover);
  display: flex;
  align-items: center;
  justify-content: center;
}

.links-item-name {
  font-size: var(--sc-text-base);
  font-weight: 600;
  color: var(--v-text_primary-base);
  text-transform: capitalize;
  min-width: 80px;
}

.links-portal-group {
  flex-shrink: 0;
}

.links-portal-toggle {
  border-radius: var(--sc-radius-sm) !important;
  overflow: hidden;
  border: 1px solid var(--sc-border-default) !important;

  .v-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
    font-size: 0.7rem !important;
    min-width: 40px !important;
    height: 26px !important;
    padding: 0 10px !important;
    background: transparent !important;
    color: var(--v-text_secondary-base) !important;
    border-color: var(--sc-border-default) !important;

    &:hover {
      background: var(--sc-surface-tint-hover) !important;
    }
  }

  .v-btn--active {
    background: var(--v-primary-base) !important;
    color: #fff !important;
  }
}

.links-item-right {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  justify-content: flex-end;
}

.links-url {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--v-primary-base);
  cursor: pointer;
  font-size: 0.75rem;
  font-family: monospace;

  &:hover {
    text-decoration: underline;
  }
}

.links-copy-btn {
  flex-shrink: 0;
  background: var(--sc-surface-tint-hover) !important;
  transition: all var(--sc-duration-base) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-color-primary-rgb),0.15) !important;
    .v-icon { color: var(--v-primary-base) !important; }
  }
}

/* Empty */
</style>
